import React from 'react';

import { Box, Typography, Divider, Tooltip } from '@mui/material';
import _ from 'lodash';
import { Link, useNavigate } from 'react-router-dom';
import { Pages } from 'src/common/constants/Pages';
import { SheetCode } from 'src/common/types/Codes';
import { SingleSearchResult } from 'src/common/types/SearchResult';
import { displayDateString } from 'src/common/utils/DateUtils';
import StatusLabel from 'src/florence/components/StatusLabel/StatusLabel';
import { getSheetName } from 'src/florence/utils/getSheetName';

type Props = {
  searchResult: SingleSearchResult;
};

type SavedSheetGroup = {
  mmText: string; // - Format example: (Modified - 32; Multiplied - 0)
  codes: SheetCode[]; // - Format example: 92544; 92544
};

const modifierText = (modifiers: string[], multiplier: number) => {
  if (_.isEmpty(modifiers) && multiplier <= 1) return '';
  if (_.isEmpty(modifiers)) return '';
  const multStr = multiplier <= 1 ? ')' : ` Units - ${multiplier})`;
  const modStr = modifiers.length > 1 ? 'Modifiers' : 'Modifier';
  return (
    '(' +
    modStr +
    ' - ' +
    modifiers.join('; ') +
    `${multiplier <= 1 ? '' : '; '}` +
    multStr
  );
};

const SearchResultCell: React.FC<Props> = ({ searchResult }) => {
  const navigate = useNavigate();

  const savedSheetGroups: SavedSheetGroup[] = React.useMemo(
    () =>
      _.chain(searchResult.sheet?.sheetCodes)
        .groupBy(
          (sheetCode) =>
            `${modifierText(sheetCode.modifiers, sheetCode.multiplier)}`
        )
        .map((codes, mmText) => {
          return { mmText, codes };
        })
        .value(),
    [searchResult.sheet?.sheetCodes]
  );

  const CellCaption = (group: SavedSheetGroup, index: number) => {
    const codes = group.codes.map((c) => c.code);
    const tooltip = group.codes.map((c) => c.description).join('. \n') + '.';
    return (
      <Tooltip
        key={index}
        enterDelay={500}
        disableFocusListener
        disableTouchListener
        title={<span style={{ whiteSpace: 'pre-line' }}>{tooltip}</span>}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            whiteSpace: 'break-spaces',
          }}
        >
          <Typography fontFamily='SFProDisplay-Bold' color='primary.main'>
            {codes.join('; ')}{' '}
          </Typography>
          <Typography color='textFlorence.main'>
            {group.mmText}
            {index === savedSheetGroups.length - 1 ? '' : ';'}{' '}
          </Typography>
        </div>
      </Tooltip>
    );
  };

  const getURL = React.useCallback(() => {
    return Pages.getRoute(Pages.result, searchResult.id);
  }, [searchResult.id]);

  const onClickOpenNote = () => {
    navigate(getURL());
  };

  return (
    <Box className='table-view-cell-container-white'>
      <Box className='row' sx={{ mb: '7px', justifyContent: 'space-between' }}>
        <Box className='row'>
          <Link
            color='#002145D9'
            style={{
              marginRight: '15px',
              fontSize: '16px',
              fontFamily: 'SFProDisplay-Bold',
              color: 'rgba(0, 33, 69, 0.85)',
              textDecoration: 'none',
            }}
            to={getURL()}
            onClick={() => onClickOpenNote()}
          >
            {searchResult.sheet
              ? getSheetName(searchResult.sheet)
              : '(Untitled)'}
          </Link>
          <StatusLabel status={searchResult.sheet?.status ?? 'Search'} />
        </Box>
      </Box>
      <Divider />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: '15px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Typography variant='body2' color='textFlorence.main'>
            {searchResult.createdAt
              ? displayDateString(searchResult.createdAt, true)
              : '01/01/2000'}
          </Typography>
          <Divider flexItem sx={{ mx: '15px' }} orientation='vertical' />
          {savedSheetGroups.map(CellCaption)}
        </Box>
      </Box>
    </Box>
  );
};

export default SearchResultCell;
